import React, { useState, useEffect } from "react"
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Typography,
  Avatar,
  Chip,
  Collapse,
  IconButton,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import TagIcon from "@mui/icons-material/Tag"
import { useTranslation } from "react-i18next"
import {
  getAllCategoryOptions,
  getProductCategories,
  saveProductCategories,
} from "helpers/productCategoryApi"

/**
 * Component to assign product categories to a specific product
 * Uses M2M structure: product -> category_option (via product_categories_mapping)
 * @param {number} productId - The product ID to manage categories for
 * @param {function} onClose - Callback when done
 */
const ProductCategoryAssigner = ({ productId, onClose }) => {
  const { t } = useTranslation()
  const [categoryGroups, setCategoryGroups] = useState({})
  const [selectedOptionIds, setSelectedOptionIds] = useState(new Set())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const [expanded, setExpanded] = useState({})

  useEffect(() => {
    loadData()
  }, [productId])

  const loadData = async () => {
    setLoading(true)
    try {
      // Load all categories with options grouped by name
      const allCats = await getAllCategoryOptions()
      setCategoryGroups(allCats)

      // Load current product category assignments
      const productCats = await getProductCategories(productId)
      const selectedIds = new Set(productCats.map(cat => cat.category_option_id))
      setSelectedOptionIds(selectedIds)

      // Initially expand all categories
      const expandedState = {}
      for (const catName of Object.keys(allCats)) {
        expandedState[catName] = true
      }
      setExpanded(expandedState)
    } catch (error) {
      setMessage({ type: "error", text: t("Error loading categories") })
      console.error("Error loading data:", error)
    } finally {
      setLoading(false)
    }
  }

  const toggleExpanded = (categoryName) => {
    setExpanded(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName],
    }))
  }

  const handleOptionChange = (optionId, checked) => {
    const newSelected = new Set(selectedOptionIds)
    if (checked) {
      newSelected.add(optionId)
    } else {
      newSelected.delete(optionId)
    }
    setSelectedOptionIds(newSelected)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await saveProductCategories(productId, Array.from(selectedOptionIds))
      setMessage({ type: "success", text: t("Categories saved successfully") })
      setTimeout(() => {
        if (onClose) onClose()
      }, 1500)
    } catch (error) {
      setMessage({ type: "error", text: t("Error saving categories") })
      console.error("Error saving:", error)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Card elevation={0} sx={{ border: "1px solid #e2e8f0" }}>
      <CardHeader
        avatar={
          <Avatar sx={{ width: 36, height: 36, bgcolor: "#e9d5ff", color: "#6b21a8" }}>
            <TagIcon sx={{ fontSize: 18 }} />
          </Avatar>
        }
        title={t("Product Categories")}
        subheader={t("Assign product to category groups")}
        sx={{ pb: 1 }}
      />
      <CardContent>
        <Stack spacing={2}>
          {message && (
            <Alert severity={message.type} onClose={() => setMessage(null)}>
              {message.text}
            </Alert>
          )}

          {/* Display selected categories as chips */}
          {selectedOptionIds.size > 0 && (
            <Box sx={{ pb: 1, borderBottom: "1px solid #e2e8f0" }}>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, display: "block", mb: 1 }}>
                {t("Selected")} ({selectedOptionIds.size})
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {Array.from(selectedOptionIds).map(optionId => {
                  // Find the option and category name
                  let optionName = ""
                  let categoryName = ""
                  for (const [catName, catData] of Object.entries(categoryGroups)) {
                    const option = catData.options.find(opt => opt.id === optionId)
                    if (option) {
                      optionName = option.name
                      categoryName = catName
                      break
                    }
                  }
                  return (
                    <Chip
                      key={optionId}
                      label={`${categoryName}: ${optionName}`}
                      onDelete={() => handleOptionChange(optionId, false)}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.75rem" }}
                    />
                  )
                })}
              </Box>
            </Box>
          )}

          {/* Category groups with checkboxes */}
          {Object.keys(categoryGroups).length === 0 ? (
            <Typography color="textSecondary">{t("No categories available")}</Typography>
          ) : (
            <Stack spacing={1.5}>
              {Object.entries(categoryGroups).map(([categoryName, categoryData]) => (
                <Box key={categoryName} sx={{ border: "1px solid #e2e8f0", borderRadius: 1 }}>
                  <Box
                    onClick={() => toggleExpanded(categoryName)}
                    sx={{
                      p: 1.5,
                      bgcolor: "#f8fafc",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      "&:hover": { bgcolor: "#f1f5f9" },
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                      {categoryName}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        {categoryData.options.filter(opt => selectedOptionIds.has(opt.id)).length} / {categoryData.options.length}
                      </Typography>
                      <IconButton size="small" sx={{ p: 0 }}>
                        {expanded[categoryName] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Box>
                  </Box>

                  <Collapse in={expanded[categoryName]} timeout="auto" unmountOnExit>
                    <Box sx={{ p: 1.5, bgcolor: "#ffffff" }}>
                      <FormGroup>
                        {categoryData.options.map(option => (
                          <FormControlLabel
                            key={option.id}
                            control={
                              <Checkbox
                                checked={selectedOptionIds.has(option.id)}
                                onChange={(e) =>
                                  handleOptionChange(option.id, e.target.checked)
                                }
                                size="small"
                              />
                            }
                            label={
                              <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                                {option.name}
                              </Typography>
                            }
                            sx={{ my: 0.5 }}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  </Collapse>
                </Box>
              ))}
            </Stack>
          )}

          {/* Action buttons */}
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={24} /> : t("Save")}
            </Button>
            {onClose && (
              <Button variant="outlined" onClick={onClose}>
                {t("Cancel")}
              </Button>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}

export default ProductCategoryAssigner
